/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import LinkIconButton from '@atlaskit/button/icon/link';
import { cssMap, cx, jsx } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Image from '@atlaskit/image/image';
import { type ExternalImageIdentifier } from '@atlaskit/media-client/identifier';
import { type MediaClientConfig } from '@atlaskit/media-core/auth';
import MediaViewer from '@atlaskit/media-viewer/media-viewer-loader';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { AppIcon } from '../app-icon/app-icon';

const mediaClientConfig: MediaClientConfig = {
	authProvider: () => Promise.reject(new Error('Not required for external images')),
};

const messages = defineMessages({
	viewImage: {
		id: 'smart-knowledge-cards.media.image-chat-result.viewImage',
		defaultMessage: 'View image: {imageName}',
		description: 'Accessible label for opening an image search result in the full-screen viewer.',
	},
	visitSource: {
		id: 'smart-knowledge-cards.media.image-chat-result.visitSource',
		defaultMessage: 'Visit: {sourceTitle}',
		description:
			'Accessible label for the external-link button overlaid on an image search result. The placeholder {sourceTitle} is the source page title.',
	},
});

const styles = cssMap({
	// Figma image card is 336×149. Width fills the message column and the height stays
	// 149px, so every result shares one frame and the photo is cropped inside it.
	frame: {
		borderRadius: token('radius.large'),
		height: '149px',
		overflow: 'hidden',
		position: 'relative',
		width: '100%',
	},
	imageButton: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		borderStyle: 'none',
		cursor: 'pointer',
		display: 'block',
		height: '100%',
		inset: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		position: 'absolute',
		width: '100%',
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.negative.025'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
	},
	image: {
		backgroundColor: token('elevation.surface.sunken'),
		display: 'block',
		height: '100%',
		objectFit: 'cover',
		objectPosition: 'center',
		width: '100%',
	},
	overlayBlanket: {
		// @ts-expect-error -- `color.blanket` is a valid token but not in cssMap's strict backgroundColor union
		backgroundColor: token('color.blanket'),
		borderRadius: token('radius.large'),
		inset: token('space.0'),
		pointerEvents: 'none',
		position: 'absolute',
	},
	overlayHidden: {
		opacity: 0,
		pointerEvents: 'none',
		visibility: 'hidden',
	},
	overlayVisible: {
		opacity: 1,
		visibility: 'visible',
	},
	iconButtonOverlay: {
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		position: 'absolute',
		right: token('space.100'),
		top: token('space.100'),
		zIndex: 1,
		// Draw the focus ring on the wrapper so overflow:hidden on the frame does not clip it.
		// @ts-expect-error -- cssMap types do not support combinators or type selectors inside :has()
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:has(:focus-visible)': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.negative.025'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& :focus-visible': {
			outlineStyle: 'none',
		},
	},
	iconButtonOverlayVisible: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		pointerEvents: 'auto',
	},
	bottomBar: {
		alignItems: 'center',
		backgroundColor: token('elevation.surface.sunken'),
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		bottom: token('space.0'),
		display: 'flex',
		left: token('space.0'),
		minHeight: '32px',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		pointerEvents: 'none',
		position: 'absolute',
		right: token('space.0'),
		zIndex: 1,
	},
	attributionTitle: {
		minWidth: 0,
	},
});

export interface ImageChatResultProps {
	/** Accessible alternative text describing the image. */
	altText: string;
	/** URL of the image search result. */
	imageUrl: string;
	/** Called when the image preview is opened. */
	onImageClick?: () => void;
	/** Called when the source-page link is activated. */
	onSourceClick?: () => void;
	/** Product that owns the source page. */
	product?: string;
	/** Title of the source page. */
	sourceTitle?: string;
	/** URL of the source page. */
	sourceUrl?: string;
}

/**
 * Renders one image-search result in a fixed 149px frame that fills the message width.
 * The photo is cropped to that frame. Source name, product icon, and the external-link
 * button appear on hover or focus.
 */
export const ImageChatResult = function ImageChatResult({
	altText,
	imageUrl,
	product,
	sourceTitle,
	sourceUrl,
	onImageClick,
	onSourceClick,
}: ImageChatResultProps): JSX.Element {
	const { formatMessage } = useIntl();
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const [isOverlayVisible, setIsOverlayVisible] = useState(false);
	const triggerRef = useRef<Element | null>(null);

	const identifier: ExternalImageIdentifier = useMemo(
		() => ({
			mediaItemType: 'external-image',
			dataURI: imageUrl,
			name: altText,
		}),
		[altText, imageUrl],
	);

	const openViewer = useCallback(() => {
		triggerRef.current = getDocument()?.activeElement ?? null;
		setIsViewerOpen(true);
		onImageClick?.();
	}, [onImageClick]);

	const closeViewer = useCallback(() => {
		const trigger = triggerRef.current;
		setIsViewerOpen(false);
		requestAnimationFrame(() => {
			if (trigger instanceof HTMLElement) {
				trigger.focus();
			}
		});
	}, []);

	const showOverlay = useCallback(() => setIsOverlayVisible(true), []);
	const hideOverlay = useCallback(() => setIsOverlayVisible(false), []);
	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLElement>) => {
			if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
				return;
			}
			hideOverlay();
		},
		[hideOverlay],
	);

	const attributionTitle = sourceTitle || product;
	const showSourceChrome = Boolean(attributionTitle || sourceUrl);

	return (
		<Box
			xcss={styles.frame}
			testId="image-chat-result"
			onMouseEnter={showOverlay}
			onMouseLeave={hideOverlay}
			onFocus={showOverlay}
			onBlur={handleBlur}
		>
			<Pressable
				xcss={styles.imageButton}
				onClick={openViewer}
				aria-label={formatMessage(messages.viewImage, { imageName: altText })}
			>
				{/* eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides */}
				<Image src={imageUrl} alt={altText} css={styles.image} />
			</Pressable>
			{showSourceChrome ? (
				<Box
					testId="image-chat-result-blanket"
					xcss={cx(
						styles.overlayBlanket,
						isOverlayVisible ? styles.overlayVisible : styles.overlayHidden,
					)}
				/>
			) : null}
			{sourceUrl ? (
				<Box
					testId="image-chat-result-source"
					xcss={cx(
						styles.iconButtonOverlay,
						isOverlayVisible ? styles.iconButtonOverlayVisible : styles.overlayHidden,
					)}
				>
					<LinkIconButton
						href={sourceUrl}
						icon={LinkExternalIcon}
						isTooltipDisabled={false}
						label={formatMessage(messages.visitSource, {
							sourceTitle: attributionTitle || altText,
						})}
						onClick={onSourceClick}
						rel="noopener noreferrer"
						spacing="default"
						target="_blank"
					/>
				</Box>
			) : null}
			{attributionTitle ? (
				<Box
					aria-hidden={sourceUrl ? true : undefined}
					testId="image-chat-result-attribution"
					xcss={cx(
						styles.bottomBar,
						isOverlayVisible ? styles.overlayVisible : styles.overlayHidden,
					)}
				>
					<Inline alignBlock="center" space="space.050">
						{product ? <AppIcon app={product} size="xsmall" /> : null}
						<Box xcss={styles.attributionTitle}>
							<Text color="color.text.subtle" maxLines={1} size="small">
								{attributionTitle}
							</Text>
						</Box>
					</Inline>
				</Box>
			) : null}
			{isViewerOpen ? (
				<MediaViewer
					mediaClientConfig={mediaClientConfig}
					selectedItem={identifier}
					items={[identifier]}
					collectionName=""
					onClose={closeViewer}
				/>
			) : null}
		</Box>
	);
};
